// ✅ src/pages/Inventory/BillPassingAdd.jsx

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import {
  ArrowLeft,
  Search,
  RefreshCw,
  Package,
  Check,
  ChevronDown,
  FileText,
} from "lucide-react";

const API_BASE = "http://nissi-india.com/api/billPass";

const today = () => new Date().toISOString().split("T")[0];
const sevenBack = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().split("T")[0];
};

const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleDateString("en-GB");
};

const BillPassingAdd = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* session */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  /* filters */
  const [filters, setFilters] = useState({
    fromDate: sevenBack(),
    toDate: today(),
    supplierId: "",
    supplierName: "",
    store: "",
    projectCode: "",
    status: "1",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    fromDate: sevenBack(),
    toDate: today(),
    supplierId: "",
    supplierName: "",
    store: "",
    projectCode: "",
    status: "1",
  });

  /* dropdowns */
  const [supplierList, setSupplierList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [projectList, setProjectList] = useState([]);

  /* GRN display values */
  const [grnNo, setGrnNo] = useState("%");
  const [grnInsNo, setGrnInsNo] = useState("%");
  const [grnConNo, setGrnConNo] = useState("%");

  /* grid */
  const [grnData, setGrnData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [grnLoading, setGrnLoading] = useState(false);

  const initialLoadDone = useRef(false);
  const [isInitialReady, setIsInitialReady] = useState(false);

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API_BASE}/bp_getSuppliers`, {
        params: { branchid: branchId },
        headers: commonHeaders,
      });
      setSupplierList(res.data?.data?.stores || []);
    } catch (err) {
      console.error("Supplier fetch error:", err);
      toast.error("Failed to load suppliers");
    }
  };

  const fetchStores = async () => {
    try {
      const res = await axios.get(`${API_BASE}/bp_getStores`, {
        params: {
          branchid: branchId,
          branchtype: branchType,
        },
        headers: commonHeaders,
      });
      setStoreList(res.data?.data || []);
    } catch (err) {
      console.error("Store fetch error:", err);
      toast.error("Failed to load stores");
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API_BASE}/bp_projectList`, {
        params: { branchid: branchId },
        headers: commonHeaders,
      });
      setProjectList(res.data?.data || []);
    } catch (err) {
      console.error("Project fetch error:", err);
    }
  };

  const fetchGRNsByDate = async (from, to, supplierId = "") => {
    try {
      setGrnLoading(true);

      const params = {
        branchid: branchId,
        dtfrom: from,
        dtto: to,
      };

      if (supplierId) {
        params.supplierid = supplierId;
      }

      const res = await axios.get(`${API_BASE}/bp_branchGRNno`, {
        params,
        headers: commonHeaders,
      });

      const d = res.data?.data || {};
      setGrnNo(d.grn?.[0]?.fld_GRNno || d.grnno || "%");
      setGrnInsNo(d.grnIns?.[0]?.fld_GRNInsno || d.grnins || "%");
      setGrnConNo(d.grnConf?.[0]?.fld_GRNconfno || "%");
    } catch (err) {
      console.error("GRN fetch error:", err);
    } finally {
      setGrnLoading(false);
    }
  };

  const handleSupplierChange = async (value) => {
    const found = supplierList.find(
      (s) => String(s.fld_supplierid) === String(value)
    );

    setFilters((prev) => ({
      ...prev,
      supplierId: value,
      supplierName: found?.fld_suppliername || "",
    }));

    setGrnData([]);
    setSelectedRows([]);

    if (!value) {
      fetchGRNsByDate(filters.fromDate, filters.toDate);
      return;
    }

    await fetchGRNsByDate(filters.fromDate, filters.toDate, value);
  };

  const handleFromDateChange = async (value) => {
    setFilters((prev) => ({ ...prev, fromDate: value }));
    if (value && filters.toDate) {
      await fetchGRNsByDate(value, filters.toDate, filters.supplierId);
    }
  };

  const handleToDateChange = async (value) => {
    setFilters((prev) => ({ ...prev, toDate: value }));
    if (filters.fromDate && value) {
      await fetchGRNsByDate(filters.fromDate, value, filters.supplierId);
    }
  };

const handleSearch = async () => {
  if (!filters.supplierId) {
    toast.warning("Please select a Supplier");
    return;
  }

  if (!filters.fromDate || !filters.toDate) {
    toast.warning("Please select From Date and To Date");
    return;
  }

  if (new Date(filters.fromDate) > new Date(filters.toDate)) {
    toast.warning("To Date should be greater than From Date");
    return;
  }

  setLoading(true);
  setSelectedRows([]);

  try {
    const payload = {
      branchid: String(branchId),
      store: filters.store ? String(filters.store) : "%",
      project: filters.projectCode ? String(filters.projectCode) : "%",
      grnInsid: "%",                          // ← fixed name + value
      grnno: "%",                             // ← do not hardcode "2"
      dtfrom: filters.fromDate,
      dtto: filters.toDate,
      supplier: String(filters.supplierId),
      vatapproved: parseInt(filters.status, 10) || 1,
    };

    console.log("bp_grnGrid payload =>", payload);

    const res = await axios.post(`${API_BASE}/bp_grnGrid`, payload, {
      headers: commonHeaders,
    });

    const result = Array.isArray(res.data?.data) ? res.data.data : [];
    setGrnData(result);

    if (result.length === 0) {
      toast.info("No GRN records found for selected criteria");
    } else {
      toast.success(`${result.length} GRN record(s) found`);
    }

    setAppliedFilters({ ...filters });
  } catch (err) {
    console.error("GRN grid fetch error:", err);
    toast.error(
      err?.response?.data?.details ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to fetch GRN data"
    );
    setGrnData([]);
  } finally {
    setLoading(false);
  }
};

  const isChecked = (item) =>
    selectedRows.some((r) => r.fld_GRNConfid === item.fld_GRNConfid);

  const toggleRow = (item) => {
    if (isChecked(item)) {
      setSelectedRows((prev) =>
        prev.filter((r) => r.fld_GRNConfid !== item.fld_GRNConfid)
      );
    } else {
      setSelectedRows((prev) => [...prev, item]);
    }
  };

  const toggleAll = (checked) => {
    setSelectedRows(checked ? [...grnData] : []);
  };

  const handleSaveAndContinue = () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one GRN record");
      return;
    }

    navigate("/inventory/BillPassing_EntryGST", {
      state: {
        selectedRows,
        supplierName: filters.supplierName,
        supplierId: filters.supplierId,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        branchId,
        loginUserId: userId,
      },
    });
  };

  const handleReset = async () => {
    const resetFilters = {
      fromDate: sevenBack(),
      toDate: today(),
      supplierId: "",
      supplierName: "",
      store: "",
      projectCode: "",
      status: "1",
    };

    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setGrnData([]);
    setSelectedRows([]);
    setGrnNo("%");
    setGrnInsNo("%");
    setGrnConNo("%");

    await fetchGRNsByDate(resetFilters.fromDate, resetFilters.toDate);
  };

  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([fetchSuppliers(), fetchStores(), fetchProjects()]);
      await fetchGRNsByDate(sevenBack(), today());
      setIsInitialReady(true);
    };

    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      loadInitialData();
    }
  }, []);

  return (
    <LayoutWrapper>
      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">Bill Pass Add Listing</span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header + Filters Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <Package className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Add Bill Pass</h1>
                <p
                  className={`text-sm ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Select GRN records to create a new Bill Pass
                </p>
              </div>
            </div>

            <button
              onClick={() => navigate("/inventory/BillPassing_Listing")}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                isDark
                  ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-100"
              }`}
            >
              <ArrowLeft size={16} />
              Back
            </button>
          </div>

          {/* Filters Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5">
            <div>
              <label className={labelClass}>
                Supplier <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={filters.supplierId}
                  onChange={(e) => handleSupplierChange(e.target.value)}
                  className={`${inputClass} appearance-none pr-10`}
                >
                  <option value="">Select Supplier</option>
                  {supplierList.map((s) => (
                    <option key={s.fld_supplierid} value={s.fld_supplierid}>
                      {s.fld_suppliername}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={16}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Store</label>
              <div className="relative">
                <select
                  value={filters.store}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, store: e.target.value }))
                  }
                  className={`${inputClass} appearance-none pr-10`}
                >
                  <option value="">All Stores</option>
                  {storeList.map((s) => (
                    <option key={s.fld_storeid} value={s.fld_storeid}>
                      {s.fld_storename}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={16}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Project</label>
              <div className="relative">
                <select
                  value={filters.projectCode}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      projectCode: e.target.value,
                    }))
                  }
                  className={`${inputClass} appearance-none pr-10`}
                >
                  <option value="">All Projects</option>
                  {projectList.map((p) => (
                    <option
                      key={p.fld_projectid || p.value}
                      value={p.fld_projectid || p.value}
                    >
                      {p.fld_projectname || p.label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={16}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>From Date</label>
              <input
                type="date"
                value={filters.fromDate}
                onChange={(e) => handleFromDateChange(e.target.value)}
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>To Date</label>
              <input
                type="date"
                value={filters.toDate}
                onChange={(e) => handleToDateChange(e.target.value)}
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>VAT Status</label>
              <div className="relative">
                <select
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, status: e.target.value }))
                  }
                  className={`${inputClass} appearance-none pr-10`}
                >
                  <option value="1">Approved</option>
                  <option value="0">DisApproved</option>
                </select>
                <ChevronDown
                  size={16}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
            </div>
          </div>

          {/* Readonly GRN values */}
          <div className="mt-6 pt-5 border-t border-slate-200 dark:border-slate-700 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { label: "GRN No", value: grnNo },
              { label: "GRN INS No", value: grnInsNo },
              { label: "GRN CON No", value: grnConNo },
            ].map(({ label, value }) => (
              <div key={label}>
                <label className={labelClass}>{label}</label>
                {grnLoading ? (
                  <Skeleton
                    height={42}
                    borderRadius={8}
                    baseColor={isDark ? "#374151" : "#f3f4f6"}
                  />
                ) : (
                  <input
                    readOnly
                    value={value === "%" ? "—" : value}
                    className={`${inputClass} cursor-not-allowed opacity-70`}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">
            <button
              onClick={handleReset}
              className={`px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                isDark
                  ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-100"
              }`}
            >
              Reset
            </button>

            <button
              onClick={handleSearch}
              disabled={loading}
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
            >
              {loading ? (
                <RefreshCw size={16} className="animate-spin" />
              ) : (
                <Search size={16} />
              )}
              {loading ? "Searching..." : "Search Records"}
            </button>
          </div>
        </div>

        {/* GRN Grid Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                ></div>
                <h3 className="text-xl font-bold">GRN Records</h3>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {grnData.length} records
                </span>
              </div>

              <div
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold ${
                  selectedRows.length > 0
                    ? isDark
                      ? "bg-green-900/40 text-green-300"
                      : "bg-green-100 text-green-700"
                    : isDark
                    ? "bg-gray-700 text-gray-300"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                <Check size={14} />
                {selectedRows.length} selected
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1000px]">
              <thead
                className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
              >
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                    <input
                      type="checkbox"
                      checked={
                        grnData.length > 0 &&
                        selectedRows.length === grnData.length
                      }
                      onChange={(e) => toggleAll(e.target.checked)}
                      className="w-4 h-4 rounded"
                    />
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                    S.No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    GRN Confirm No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    GRN No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    Branch Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    Invoice No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    Invoice Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    DC No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    DC Date
                  </th>
                </tr>
              </thead>

              <tbody
                className={`divide-y ${
                  isDark ? "divide-gray-600" : "divide-gray-200"
                }`}
              >
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr
                      key={i}
                      className={isDark ? "bg-gray-800/20" : "bg-white"}
                    >
                      {[...Array(10)].map((_, j) => (
                        <td key={j} className="px-4 py-3">
                          <Skeleton
                            height={20}
                            baseColor={isDark ? "#374151" : "#f3f4f6"}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : grnData.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <Package size={48} className="text-gray-400" />
                        <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                          No GRN records yet
                        </div>
                        <div className="text-sm text-gray-500">
                          Select supplier and click search to load pending GRNs
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  grnData.map((item, idx) => {
                    const checked = isChecked(item);

                    return (
                      <tr
                        key={item.fld_GRNConfid || idx}
                        onClick={() => toggleRow(item)}
                        className={`cursor-pointer transition ${
                          checked
                            ? isDark
                              ? "bg-blue-900/30"
                              : "bg-blue-50"
                            : isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRow(item)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 rounded"
                          />
                        </td>

                        <td className="px-4 py-3 text-center font-medium">
                          {idx + 1}
                        </td>

                        <td className="px-4 py-3 font-semibold text-blue-600">
                          {fmt(item.fld_GRNconfno)}
                        </td>
                        <td className="px-4 py-3">{fmt(item.fld_GRNno)}</td>
                        <td className="px-4 py-3">{fmt(item.fld_branchname)}</td>
                        <td className="px-4 py-3">{fmtDate(item.fld_Date)}</td>
                        <td className="px-4 py-3">{fmt(item.fld_Invno)}</td>
                        <td className="px-4 py-3">
                          {fmtDate(item.fld_InvDate)}
                        </td>
                        <td className="px-4 py-3">{fmt(item.fld_DCNo)}</td>
                        <td className="px-4 py-3">
                          {fmtDate(item.fld_DCDate)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Action Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p
              className={`text-sm ${
                isDark ? "text-gray-300" : "text-gray-700"
              }`}
            >
              {selectedRows.length > 0
                ? `${selectedRows.length} record(s) selected for bill passing`
                : "Click rows or use checkboxes to select GRN records"}
            </p>

            <button
              onClick={handleSaveAndContinue}
              disabled={selectedRows.length === 0}
              className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold rounded-xl shadow transition ${
                selectedRows.length > 0
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-gray-400 text-white cursor-not-allowed"
              }`}
            >
              <Check size={16} />
              Save & Continue
            </button>
          </div>

          {/* Stats */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div
                className={`p-3 rounded-xl ${
                  isDark ? "bg-gray-800/30" : "bg-gray-50"
                }`}
              >
                <div className="text-2xl font-bold text-blue-600">
                  {grnData.length}
                </div>
                <div className="text-xs text-gray-500">Loaded GRNs</div>
              </div>
              <div
                className={`p-3 rounded-xl ${
                  isDark ? "bg-gray-800/30" : "bg-gray-50"
                }`}
              >
                <div className="text-2xl font-bold text-green-600">
                  {selectedRows.length}
                </div>
                <div className="text-xs text-gray-500">Selected</div>
              </div>
              <div
                className={`p-3 rounded-xl ${
                  isDark ? "bg-gray-800/30" : "bg-gray-50"
                }`}
              >
                <div className="text-2xl font-bold text-purple-600 truncate px-2">
                  {filters.supplierName || "—"}
                </div>
                <div className="text-xs text-gray-500">Supplier</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
        theme={isDark ? "dark" : "light"}
        toastStyle={{
          borderRadius: "12px",
          fontSize: "14px",
        }}
      />
    </LayoutWrapper>
  );
};

export default BillPassingAdd;